import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CreditCard, AlertTriangle, Check } from 'lucide-react';

const SubscriptionModal = ({ isOpen, onClose, variant = 'onboarding' }) => {
  const navigate = useNavigate();
  const isExpired = variant === 'expired';

  if (!isOpen) return null;

  const handleSubscribe = () => {
    const onSubscriptionPage = window.location.pathname.startsWith('/vendor/subscription');

    if (isExpired) {
      if (!onSubscriptionPage) {
        navigate('/vendor/subscription');
      }
      onClose?.();
      return;
    }

    navigate('/vendor/subscription');
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[99999] p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="subscription-modal-title"
      >
        <div className="relative p-6 pb-4">
          {!isExpired && (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          )}

          <div className="flex items-center justify-center mb-4">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center ${
                isExpired
                  ? 'bg-amber-100'
                  : 'bg-gradient-to-r from-primary-500 to-primary-600'
              }`}
            >
              {isExpired ? (
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              ) : (
                <CreditCard className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          <h2
            id="subscription-modal-title"
            className="text-2xl font-bold text-gray-900 text-center mb-2"
          >
            {isExpired ? 'Your subscription has ended' : 'Start Your Journey'}
          </h2>
          <p className="text-gray-600 text-center text-sm leading-relaxed">
            {isExpired
              ? 'Upgrade your plan to accept new client bookings, manage orders, and use your vendor dashboard again.'
              : 'Choose a subscription plan to start offering your services and grow your business.'}
          </p>
        </div>

        <div className="px-6 pb-6">
          {!isExpired && (
            <div className="space-y-3 mb-6">
              {[
                'Accept and manage client bookings',
                'Portfolio and service management',
                'Analytics dashboard',
                'Priority support',
              ].map((label) => (
                <div key={label} className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-gray-700 text-sm">{label}</span>
                </div>
              ))}
            </div>
          )}

          {isExpired && (
            <ul className="mb-6 space-y-2 text-sm text-gray-700 list-disc list-inside">
              <li>You cannot accept or update client bookings</li>
              <li>Services, invoices, and payouts are paused</li>
              <li>Choose a plan below to restore full access</li>
            </ul>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleSubscribe}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              {isExpired ? 'Upgrade plan now' : 'View plans & subscribe'}
            </button>

            {!isExpired && (
              <button
                type="button"
                onClick={onClose}
                className="w-full text-gray-600 py-2 px-4 rounded-lg font-medium hover:text-gray-800 transition-colors"
              >
                Maybe later
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
